"use client";

import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { PACIC_CONCEPTS, type BasketConcept } from "@/lib/canasta-basica/pacic";
import { cn } from "@/lib/utils";

interface Props {
  /** Concepto seleccionado (un solo valor). */
  active?: string;
  /** Productos marcados por concepto con los filtros actuales. */
  counts?: Record<string, number>;
}

/** Los 24 productos de la canasta básica PROFECO, agrupados para los chips. */
const GROUPS: Array<{ name: string; slugs: string[] }> = [
  { name: "Granos y abarrotes", slugs: ["arroz", "frijol", "pasta", "azucar", "aceite", "atun", "sardina"] },
  { name: "Tortilla, pan, leche y huevo", slugs: ["tortilla", "pan-caja", "leche", "huevo"] },
  { name: "Carnes", slugs: ["carne-res", "cerdo", "pollo"] },
  {
    name: "Frutas y verduras",
    slugs: ["jitomate", "cebolla", "chile-jalapeno", "papa", "zanahoria", "limon", "manzana", "platano"],
  },
  { name: "Higiene", slugs: ["jabon", "papel-higienico"] },
];

const BY_SLUG = new Map(PACIC_CONCEPTS.map((c) => [c.slug, c]));

export function BasketChips({ active, counts }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const setItem = (slug: string | null) => {
    const params = new URLSearchParams(searchParams.toString());
    if (slug) params.set("item", slug);
    else params.delete("item");
    params.delete("page");
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  const total = counts ? Object.values(counts).reduce((s, n) => s + n, 0) : undefined;

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setItem(null)}
        className={cn(
          "inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-semibold transition-colors",
          !active ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted",
        )}
      >
        🧺 Toda la canasta básica
        {total !== undefined && <span className="opacity-70">{total}</span>}
      </button>
      {GROUPS.map((group) => (
        <div key={group.name}>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1.5">{group.name}</p>
          <div className="flex flex-wrap gap-1.5">
            {group.slugs
              .map((s) => BY_SLUG.get(s))
              .filter((c): c is BasketConcept => Boolean(c))
              .map((concept) => (
                <BasketChip
                  key={concept.slug}
                  concept={concept}
                  count={counts?.[concept.slug]}
                  active={active === concept.slug}
                  onClick={() => setItem(active === concept.slug ? null : concept.slug)}
                />
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function BasketChip({
  concept,
  count,
  active,
  onClick,
}: {
  concept: BasketConcept;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  const empty = count === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={empty && !active}
      className={cn(
        "inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-semibold transition-colors",
        active ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted",
        empty && !active && "opacity-40 cursor-not-allowed",
      )}
    >
      <span aria-hidden>{concept.glyph}</span>
      {concept.label}
      {count !== undefined && <span className="opacity-70">{count}</span>}
    </button>
  );
}
